import { useState } from 'react'
import { Permissions } from '../../../../types/user-types'
import SummaryTable from '../../wells/summary-table'
import NoYieldDrawDownTests from './drilling-test-view'
import { ERROR_MESSAGES } from '../../errors/messages'
import { canEditWell } from '../../../services/user-utils'
import useUser from '../../../hooks/use-user'
import { useWellId } from '../../../hooks/use-well-id'
import { ColumnHeader, TableAction } from '@msaf/core-react'

interface LithologySummaryTableProps {
  fields: Record<string, string | number | boolean>[] | null
  setFields: React.Dispatch<React.SetStateAction<Record<string, string | number | boolean>[] | null>>
  isViewMode?: boolean
  handleNoTestsCheckbox?: (checked: boolean) => void
  isNoTestsChecked?: boolean
}

export default function YieldDrawDownSummaryTable({
  fields,
  setFields,
  handleNoTestsCheckbox,
  isNoTestsChecked,
  isViewMode = false,
}: LithologySummaryTableProps) {
  const wellId = useWellId()
  const { user } = useUser()
  const [rowId, setRowId] = useState<number | null>(null)

  const onModalClose = () => {
    setRowId(null)
  }

  const rowClickAction: TableAction = {
    label: 'View',
    type: 'transitionAction',
    args: [{ constant: `wells/${wellId}/view/drilling-tests` }, { elementKey: 'testId' }],
  }

  const actions: Array<TableAction> = [
    {
      label: 'Delete',
      type: 'deleteAction',
      args: [
        {
          elementKey: 'id',
        },
      ],
      actionPermissions: [Permissions.DELETE_WELL_DATA],
    },
  ]

  const columnHeaders: Array<ColumnHeader> = [
    {
      elementKey: 'id',
      columnHeading: 'Id',
      viewColumn: 'id',
      type: 'hidden',
    },
    {
      elementKey: 'testId',
      columnHeading: 'Test id',
      viewColumn: 'testId',
      type: 'hidden',
    },
    {
      elementKey: 'testDate',
      columnHeading: 'Test date',
      viewColumn: 'testDate',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'stepNo',
      columnHeading: 'Step',
      viewColumn: 'stepNo',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'yieldValueLS',
      columnHeading: 'Yield (l/s)',
      viewColumn: 'yieldValueLS',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'yieldValueLM',
      columnHeading: 'Yield (l/min)',
      viewColumn: 'yieldValueLM',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'yieldValueCMH',
      columnHeading: 'Yield (m3/h)',
      viewColumn: 'yieldValueCMH',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'yieldValueCMD',
      columnHeading: 'Yield (m3/d)',
      viewColumn: 'yieldValueCMD',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'drawdown',
      columnHeading: 'Drawdown (M)',
      viewColumn: 'drawdown',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'stepDuration',
      columnHeading: 'Minutes',
      viewColumn: 'stepDuration',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'actions',
      columnHeading: 'Actions',
      viewColumn: 'actions',
      type: 'actions-no-data',
      actions,
    },
  ]

  const deleteAction = (id: number) => {
    setRowId(id)
  }

  return (
    <SummaryTable
      fields={fields}
      setFields={setFields}
      columnHeaders={columnHeaders}
      defaultSortBy={{
        orderColumn: 'testDate',
        orderDirection: 'desc',
      }}
      rowClickAction={rowClickAction}
      requestUrl={`wells/${wellId}/drilling-tests`}
      tableActionHandlers={{
        deleteAction,
      }}
      confirmDeleteModalProps={{
        isShowModal: !!rowId,
        requestUrl: `wells/${wellId}/drilling-tests/step/${rowId}/edit`,
        heading: 'Delete yield drawdown test step',
        bodyText: 'This will delete the yield drawdown test step. Are you sure you wish to proceed?',
        successMessage: 'Yield drawdown test step deleted',
        errorMessage: ERROR_MESSAGES.DELETE_FAILED,
        onModalClose,
      }}
      noResultsComponent={
        <NoYieldDrawDownTests
          isChecked={isNoTestsChecked}
          handleNoTestsCheckbox={handleNoTestsCheckbox}
          isViewMode={isViewMode || (!isViewMode && !canEditWell(user))}
        />
      }
      resultNameSingular='test'
      resultNamePlural='tests'
    />
  )
}
